/**
 * Real-time Supabase Subscriptions
 *
 * Wraps Supabase Realtime channels so components can receive live updates
 * for bookings, notifications, and compliance alerts without polling.
 *
 * Usage:
 *   useRealtimeBookings()       — invalidates the bookings query cache on any change
 *   useRealtimeNotifications()  — invalidates the notifications query cache on any change
 *
 * These hooks are intentionally "fire and forget" — they don't return data directly.
 * Instead they call queryClient.invalidateQueries() so the normal React Query hooks
 * pick up the fresh data automatically.
 */

import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase, isMissingSupabaseConfig } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

// ─── Bookings Real-time ───────────────────────────────────────────────────────

/**
 * Subscribes to INSERT/UPDATE/DELETE events on the `bookings` table for the
 * active organisation. Automatically invalidates the `["bookings"]` query
 * cache so the Bookings page and dashboard refresh instantly.
 *
 * Cleans up the channel subscription on unmount.
 */
export function useRealtimeBookings() {
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  // Keep a ref to the channel so we can unsubscribe on cleanup
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    // Skip when Supabase is not configured (avoids placeholder-URL WebSocket errors)
    if (isMissingSupabaseConfig) return;
    if (!activeOrgId) return;

    // Create a uniquely named channel per org to avoid conflicts
    const channel = supabase
      .channel(`realtime:bookings:${activeOrgId}`)
      .on(
        "postgres_changes",
        {
          event: "*",            // INSERT, UPDATE, DELETE
          schema: "public",
          table: "bookings",
          filter: `organization_id=eq.${activeOrgId}`,
        },
        (payload) => {
          console.log("[realtime:bookings] Change received:", payload.eventType);
          // Invalidate all booking-related queries
          qc.invalidateQueries({ queryKey: ["bookings"] });
          qc.invalidateQueries({ queryKey: ["dashboard_stats"] });
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          console.log("[realtime:bookings] Subscribed to org:", activeOrgId);
        }
        if (status === "CHANNEL_ERROR") {
          console.warn("[realtime:bookings] Channel error — will retry automatically");
        }
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch {
          // Suppress "WebSocket is closed before the connection is established"
          // race-condition errors that occur when cleanup fires during connect.
        }
        channelRef.current = null;
      }
    };
  }, [activeOrgId, qc]);
}

// ─── Notifications Real-time ──────────────────────────────────────────────────

/**
 * Subscribes to INSERT events on the `notifications` table for the current user.
 * Automatically invalidates the `["notifications"]` query so the notification
 * badge and panel update instantly when a new notification arrives.
 */
export function useRealtimeNotifications() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (isMissingSupabaseConfig) return;
    if (!user?.id) return;

    const channel = supabase
      .channel(`realtime:notifications:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log("[realtime:notifications] New notification:", payload.new);
          qc.invalidateQueries({ queryKey: ["notifications"] });
          // Also refresh the unread-count badge in TopHeader
          qc.invalidateQueries({ queryKey: ["notifications-unread"] });
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch {
          // Suppress WebSocket race-condition errors on fast unmounts.
        }
        channelRef.current = null;
      }
    };
  }, [user?.id, qc]);
}

// ─── Hosts Real-time ──────────────────────────────────────────────────────────

/**
 * Subscribes to changes on the `hosts` table (e.g. batch validation updates
 * that change a host's status). Refreshes the hosts list automatically.
 */
export function useRealtimeHosts() {
  const qc = useQueryClient();
  const { activeOrgId } = useAuth();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (isMissingSupabaseConfig) return;
    if (!activeOrgId) return;

    const channel = supabase
      .channel(`realtime:hosts:${activeOrgId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "hosts",
          filter: `organization_id=eq.${activeOrgId}`,
        },
        (payload) => {
          console.log("[realtime:hosts] Host updated:", payload.new);
          qc.invalidateQueries({ queryKey: ["hosts"] });
          qc.invalidateQueries({ queryKey: ["dashboard_stats"] });
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        try {
          supabase.removeChannel(channelRef.current);
        } catch {
          // Suppress WebSocket race-condition errors on fast unmounts.
        }
        channelRef.current = null;
      }
    };
  }, [activeOrgId, qc]);
}
